import React, { useState, useEffect, useMemo, useCallback } from "react";

import { useTranslation } from "react-i18next";
import { useLoading } from "../../context/LoadingContext";
import { useAuth } from "../../context/AuthContext";

import { useNavigate } from "react-router-dom";

import TransactionStatus from "./TransactionStatus";

import { IoMdArrowBack, IoIosWarning } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { RiInformation2Fill } from "react-icons/ri";
import FloatingMenu from "../FloatingMenu";
import ErrorBanner from "../ui/ErrorBanner";

import { formatCurrency,
    sanitizeNumberInput,
    preventInvalidKeyInput
} from "../../utils/global";

import { fetchBalance,
    withdraw,
    fetchCryptoList,
    fetchCurrencyRate
} from "../../utils/api";

import config from '../../config';

const CryptoWithdraw = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const { token } = useAuth();
    const navigate = useNavigate();

    const [error, setError] = useState({
        message: "",
        shake: false
    });
    const [wallet, setWallet] = useState({
        balance: 0,
        status: null
    });
    const [coin, setCoin] = useState({
        list: [],
        selected: null,
        network: null
    });
    const [withdrawal, setWithdrawal] = useState({
        amount: "",
        options: ["50", "100", "200", "500", "1000", "2000"]
    });
    const [address, setAddress] = useState({
        value: "",
        valid: false
    });
    
    const [rate, setRate] = useState(0);

    const clearError = useCallback(() => {
        setError(prev => ({ ...prev, message: "" }));
    }, []);

    const groupedCoins = useMemo(() => {
        const coinMap = new Map();
        coin.list.forEach((c) => {
            if (!coinMap.has(c.name)) {
                coinMap.set(c.name, {
                    name: c.name,
                    crypto_code: c.crypto_code,
                    icon: c.icon,
                    networks: [],
                });
            }
            coinMap.get(c.name).networks.push({
                id: c.id,
                symbol: c.symbol,
                min_deposit: c.min_deposit,
                max_deposit: c.max_deposit,
                deposit: c.deposit,
                icon: c.icon,
                length: c.length
            });
        });
        return Array.from(coinMap.values());
    }, [coin.list]);

    const networkOptions = coin.selected
        ? groupedCoins.find((c) => c.name === coin.selected.name)?.networks || []
        : [];

    const checkMinMaxWithdraw = useCallback((amount, balanceInUSD, selectedNetwork = coin.network) => {
        if (isNaN(amount)) {
            clearError();
            return;
        }

        const minWithdrawal = Number(selectedNetwork?.min_deposit ?? 50);
        const maxWithdrawal = Number(selectedNetwork?.max_deposit ?? 5000);

        let message = "";

        if (amount < minWithdrawal) {
            message = t("minWithdrawal", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(minWithdrawal) });
        } else if (amount > maxWithdrawal) {
            message = t("maxWithdrawal", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(maxWithdrawal) });
        } else if (amount > balanceInUSD) {
            message = t("insufficientFunds");
        }

        setError(prev => prev.message === message ? prev : { ...prev, message, shake: !!message });

        if (message) {
            setCoin((prev) => ({ ...prev, selected: null, network: null }));
            setAddress((prev) => ({ ...prev, value: "", valid: false }));
            setTimeout(() => setError(prev => ({ ...prev, shake: false })), 500);
        }
    }, [t, coin.network, wallet.balance, clearError]);

    const handleWithdrawAmountChange = useCallback((input) => {
        const inputValue = input?.target ? input.target.value : input;
        const sanitizedValue = sanitizeNumberInput(inputValue);
        setWithdrawal(prev => ({ ...prev, amount: sanitizedValue }));

        const amount = Number(sanitizedValue);
        const balanceInUSD = Number((wallet.balance / rate).toFixed(2));
        checkMinMaxWithdraw(amount, balanceInUSD);
    }, [checkMinMaxWithdraw]);

    const handleClearInput = () => setAddress({ value: "", valid: false });

    const loadData = useCallback(async () => {
        setLoading(true);
        clearError();

        try {
            const [balanceResponse, coinResponse, rateResponse] = await Promise.all([
                fetchBalance(),
                fetchCryptoList(),
                fetchCurrencyRate()
            ]);

            setWallet(prev => ({
                ...prev,
                balance: balanceResponse.success ? balanceResponse.data : 0,
            }));

            if (coinResponse.success && Array.isArray(coinResponse.data) && coinResponse.data.length > 0) {
                const filteredCoins = coinResponse.data.filter((c) => c.deposit === 1);
                setCoin((prev) => ({ ...prev, list: filteredCoins }));
            } else {
                setCoin((prev) => ({ ...prev, list: [] }));
            }

            if (rateResponse.success) {
                setRate(rateResponse.data.exchange_rate);
            } else {
                setRate(0);
            }
        } catch (error) {
            setError(prev => ({ ...prev, message: t("pleaseTryLater") }));
        } finally {
            setLoading(false);
        }
    }, [t, setLoading, clearError]);

    useEffect(() => {
        if (error.message) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [error.message]);

    useEffect(() => {
        if (token) {
            loadData();
        }
    }, [token, loadData]);

    const handleApi = useCallback(async () => {
        setLoading(true);
        clearError();

        try {
            const amountIDR = Number(withdrawal.amount) * rate;
            const response = await withdraw({
                amount: amountIDR,
                address: address.value,
                    rate : rate
            });

            if (response.success) {
                setWallet(prev => ({
                    ...prev,
                    status: {
                        status: "success",
                        method: "Crypto Withdraw",
                        title: t("submitSuccess"),
                        message: t("requestBeingProcessed"),
                        onPrimaryAction: () => navigate("/"),
                        primaryTitle: t("backToHome"),
                    },
                }));
            } else {
                const apiError = response.data;
                if (typeof apiError === "string") {
                    let msg = apiError;
                    if (apiError.toLowerCase().includes("promotion turnover target")) {
                        const match = apiError.match(/target (\d+)/);
                        msg = t("promotionTurnoverError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    } else if (apiError.toLowerCase().includes("turnover target")) {
                        const match = apiError.match(/target (\d+)/);
                        msg = t("turnoverTargetError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    } else if (apiError.toLowerCase().includes("minimum withdrawal")) {
                        const match = apiError.match(/is (\d+)/);
                        msg = t("minWithdrawalError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    }

                    setError({ message: msg });
                } else {
                    setWallet(prev => ({
                        ...prev,
                        status: {
                            method: "Crypto Withdraw",
                            title: t("encounteredError"),
                            message: t("unknownError"),
                            onPrimaryAction: () => {window.location.reload()},
                            primaryTitle: t("tryAgain"),
                            onSecondaryAction: () => {
                                if (window.Telegram?.WebApp) {
                                    window.Telegram.WebApp.openTelegramLink(config.TELEGRAM_URL);
                                } else {
                                    window.open(config.TELEGRAM_URL, "_blank");
                                }
                            },
                            secondaryTitle: t("contactUsForSupport"),
                        },
                    }));
                }
            }
        } catch (err) {
            setError(prev => ({ ...prev, message: t("failedToFetchLink") }));
        } finally {
            setLoading(false);
        }
    }, [t, navigate, setLoading, withdrawal.amount, address.value, clearError]);

    return (
        <section className="page-shell">
            <div className="page-header">
                <div
                    className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                    onClick={() => navigate("/withdraw")}
                >
                    <IoMdArrowBack className="size-sm" />
                    <span className="text-sm">
                        {t("backToLobby")}
                    </span>
                </div>
                <button className="cursor-pointer" onClick={() => navigate("/")}>
                    <IoClose className="size-lg text-color-blue" />
                </button>
            </div>

            <ErrorBanner message={error.message} />

            <div className="px-4 pt-[30px] pb-[100px]">
                {wallet.status && <TransactionStatus {...wallet.status} />}
                {!wallet.status && (
                    <>
                        <p className="mb-3 text-sm text-center font-bold text-white">
                            {t("enterWithdrawAmount")}
                        </p>
                        
                        <div className="info-banner mb-8">
                            <RiInformation2Fill className="size-[30px] text-border-yellow" />
                            <div className="flex flex-col flex-grow">
                                <span className="text-xs text-white">
                                    {t("minWithdrawal", {currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(coin.network?.min_deposit ?? 50)})}
                                </span>
                                <span className="text-xs text-white">
                                    {t("maxWithdrawal", {currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(coin.network?.max_deposit ?? 5000)})}
                                </span>
                            </div>
                        </div>

                        <div className="bg-main-gradient rounded-lg p-4 flex flex-col mt-[40px]">
                            <span className="text-xs text-white mb-3">
                                {t("cryptoRateInfo")}
                            </span>
                            <div className="bg-secondary container-equal-lg rounded-md relative flex flex-col items-start">
                                <span className="text-sm text-color-blue">
                                    {t("currentRate")}:
                                </span>
                                <span className="text-sm text-white font-bold">
                                    1 {t("USDT")} = {config.CURRENCY_ISO_CODE} {formatCurrency(rate)}
                                </span>
                            </div>
                        </div>

                        <div className="max-w-md my-6 text-center mx-auto w-fit">
                            <div
                                className={`text-[36px] font-bold text-white transition-transform duration-300 ${
                                    error.shake ? "animate-shake" : ""
                                }`}
                            >
                                <input
                                    type="text"
                                    value={withdrawal.amount || ""}
                                    onKeyDown={preventInvalidKeyInput}
                                    onChange={handleWithdrawAmountChange}
                                    placeholder="0"
                                    className="bg-transparent text-white text-[36px] font-bold text-right outline-none pr-2 w-auto min-w-[2ch] max-w-[10ch] md:max-w-[20ch]"
                                    style={{ width: `${(withdrawal.amount?.length || 1) + 1}ch` }}
                                />
                                <span className="text-white/70 pl-1">USDT</span>
                            </div>
                        </div>

                        <div className="flex flex-col text-center mb-8">
                            <span className="text-sm font-bold text-white">
                                {t("availableBalance")} :
                            </span>
                            <span className="font-bold text-color-second-light-blue">
                                {`${formatCurrency((wallet.balance / rate), config.CURRENCY_ISO_CODE)} USDT`}
                            </span>
                        </div>

                        <div className="grid grid-cols-3 gap-3 my-8">
                            {withdrawal.options.map((amount, index) => (
                                <div
                                    key={index}
                                    className="relative"
                                >
                                    {amount === "100" && (
                                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-2 py-1 text-xxs font-bold text-black uppercase rounded-full btn-gradient w-max">
                                            {t("popular")}
                                        </div>
                                    )}
                                    <div
                                        onClick={() => {
                                            handleWithdrawAmountChange(amount);
                                        }}
                                        className={`cursor-pointer p-2 h-[36px] text-center transition-colors rounded-sm ${amount === `${withdrawal.amount}` ? "btn-gradient text-black" : "bg-main-gradient text-white"}`}
                                    >
                                        <span className="text-sm font-bold">
                                            {formatCurrency(amount)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div
                            className={`transition-all duration-300 ease-in-out ${
                                !error.message && withdrawal.amount 
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                        >
                            <div className="mb-8">
                                <p className="mb-3 text-sm font-bold text-white">
                                    {t("selectCoin")}
                                </p>
                                <div className="rounded-lg bg-option-blue">
                                    {groupedCoins.map((c) => (
                                        <div
                                            key={c.name}
                                            className={`container-xs justify-start gap-2 cursor-pointer text-white 
                                                border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0
                                                ${coin.selected?.name === c.name ? "bg-option-second-selected-blue" : ""}`}
                                            onClick={() => {
                                                setCoin((prev) => ({ ...prev, selected: prev.selected?.name === c.name ? null : c, network: null }));
                                                setAddress({ value: "", valid: false });
                                                clearError();
                                            }}
                                        >
                                            <img
                                                src={`${config.APP_URL}${c.icon}`}
                                                alt={`${c.name} icon`}
                                                className="size-xl"
                                            />
                                            <div className="flex flex-col">
                                                <span className="text-sm">
                                                    {c.name}
                                                </span>
                                                <span className="text-xs text-color-blue">
                                                    {c.crypto_code}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div
                            className={`transition-all duration-300 ease-in-out ${
                                !error.message && withdrawal.amount && coin.selected && networkOptions.length > 0
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                        >
                            <div className="mb-8">
                                <p className="mb-3 text-sm font-bold text-white">
                                    {t("selectNetwork")}
                                </p>

                                <div className="rounded-lg bg-option-blue">
                                    {networkOptions.map((n) => (
                                        <div
                                            key={n.id}
                                            className={`container-equal-2xl justify-start gap-2 cursor-pointer text-white 
                                                border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0
                                                ${coin.network?.id === n.id ? "bg-option-second-selected-blue" : ""}`}
                                            onClick={() => {
                                                setCoin((prev) => ({ ...prev, network: prev.network?.id === n.id ? null : n }));
                                                setAddress((prev) => ({ ...prev, value: "", valid: false }));
                                                clearError();
                                                if (withdrawal.amount) {
                                                    checkMinMaxWithdraw(withdrawal.amount, n)
                                                }
                                            }}
                                        >
                                            <span className="text-sm">
                                                {n.symbol}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div
                            className={`transition-all duration-300 ease-in-out ${
                                !error.message && withdrawal.amount && coin.selected && coin.network
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                        >
                            <div className="bg-main-gradient rounded-lg p-4 flex flex-col">
                                <span className="mb-3 text-sm text-white">
                                    {t("enterWalletAddress")}
                                </span>

                                <div className="relative flex-grow">
                                    <textarea
                                        value={address.value}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const valid = coin.network ? value.length === coin.network.length : false;
                                            setAddress({ value, valid });
                                        }}
                                        placeholder={t("walletAddressPlaceholder")}
                                        className="w-full rounded-lg cursor-pointer bg-secondary container-equal-sm text-white focus:outline-none pr-12 resize-none overflow-auto"
                                        style={{
                                            minHeight: '40px',
                                            whiteSpace: 'pre-wrap',
                                            wordWrap: 'break-word',
                                        }}
                                        rows={Math.ceil(address.value.length / 25) || 1}
                                    />
                                    <button
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer transition-all duration-300"
                                        onClick={handleClearInput}
                                    >
                                        <IoClose
                                            className={`size-sm transition-all duration-300 text-color-blue ${
                                            address.value.length > 0 ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-180 scale-0"
                                            }`}
                                        />
                                    </button>
                                </div>

                                {address.value && (
                                    <>
                                        {address.valid && coin.network ? (
                                            <div className="rounded-lg bg-secondary mt-3">
                                                <div className="container-equal-lg justify-start gap-2">
                                                    <img
                                                        src={`${config.APP_URL}${coin.network.icon}`}
                                                        alt={`${coin.network.symbol} icon`}
                                                        className="size-xl"
                                                    />
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-white">
                                                            {(() => {
                                                                let truncatedInput;
                                                                if (address.value.length > 25) {
                                                                    const firstLine = address.value.substring(0, 25);
                                                                    const secondLine = address.value.substring(25);
                                                                    truncatedInput = (
                                                                        <>
                                                                            {firstLine}
                                                                            <br />
                                                                            {secondLine}
                                                                        </>
                                                                    );
                                                                } else {
                                                                    truncatedInput = address.value;
                                                                }
                                                                return truncatedInput;
                                                            })()}
                                                        </span>
                                                        <span className="text-xs text-color-blue">
                                                            {coin.network.symbol}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="rounded-lg bg-secondary mt-3">
                                                <div className="container-equal-lg justify-start gap-2">
                                                    <IoIosWarning className="size-xl text-color-blue" />
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-white">
                                                            {t("incorrectAddress")}
                                                        </span>
                                                        <span className="text-xs text-color-blue">
                                                            {t("incorrectAddress")}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <button
                            className={`fixed bottom-[60px] md:bottom-0 left-0 right-0 container-lg h-[60px] text-center btn-gradient transition-all duration-300 ease-in-out ${
                                !error.message && withdrawal.amount && coin.selected && coin.network && address.valid
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                            onClick={() => {
                                handleApi();
                            }}
                        >
                            <span className="text-sm font-bold">
                                {t("submit")}
                            </span>
                        </button>
                    </>
                )}
            </div>
            
            <FloatingMenu />
        </section>
    );
};

// === Export ===
export default CryptoWithdraw;